import { useAtom } from "jotai";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useCrefazCltContext } from "@/app/hooks/useCrefazCltContext";
import { currentStep } from "@/app/store";
import { orgs } from "@/app/utils/orgs";
import { phoneMask } from "@/app/utils/phoneFormater";
import { BasicInput } from "@/view/components/BasicInput";
import { BasicSelect } from "@/view/components/BasicSelect";
import CityInput from "@/view/components/CrefazClt/CityInput";
import { Button } from "@/view/components/ui/button";

const toOptions = (items: { id: number | string; nome: string }[]) =>
  items.map((item) => ({ label: item.nome, value: String(item.id) }));

interface PersonalInfoFormProps {
  visible?: boolean;
}

export default function PersonalInfoForm({ visible }: PersonalInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const {
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();

  const { items: genders } = useCrefazCltContext("sexo");
  const { items: maritalStatuses } = useCrefazCltContext("estadoCivil");
  const { items: nationalities } = useCrefazCltContext("nacionalidade");
  const { items: educationLevels } = useCrefazCltContext("grauInstrucao");
  const { items: ufs } = useCrefazCltContext("uf");

  const maritalStatus = watch("cliente.estadoCivil");
  const birthUfId = watch("cliente.naturalidadeUfId");

  const requiresSpouse =
    maritalStatuses.find((item) => String(item.id) === maritalStatus)
      ?.exigeConjuge ?? false;

  useEffect(() => {
    if (!requiresSpouse) {
      setValue("cliente.nomeConjuge", "");
    }
  }, [requiresSpouse, setValue]);

  const birthUf = ufs.find((uf) => String(uf.id) === birthUfId)?.nome;

  const clienteErrors = (errors.cliente ?? {}) as Record<
    string,
    { message?: string }
  >;

  const handleNext = async () => {
    const isValid = await trigger("cliente");

    if (isValid) setStep((state) => state + 1);
  };

  return (
    <div className={visible ? "flex flex-col gap-8" : "hidden"}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <BasicInput
          name="cliente.nome"
          label="Nome completo"
          error={clienteErrors.nome?.message}
        />
        <BasicInput
          name="cliente.nomeMae"
          label="Nome da mãe"
          error={clienteErrors.nomeMae?.message}
        />
        <BasicInput
          name="cliente.email"
          label="E-mail"
          error={clienteErrors.email?.message}
        />
        <BasicInput
          name="cliente.telefone"
          label="Telefone"
          onInput={phoneMask}
          maxLength={15}
          error={clienteErrors.telefone?.message}
        />

        <BasicInput
          name="cliente.rg"
          label="RG"
          error={clienteErrors.rg?.message}
        />
        <BasicInput
          name="cliente.rgEmissao"
          type="date"
          label="Data de emissão"
          error={clienteErrors.rgEmissao?.message}
        />
        <BasicSelect
          name="cliente.rgEmissor"
          label="Órgão emissor"
          options={orgs}
          placeholder="Selecione"
          error={clienteErrors.rgEmissor?.message}
        />
        <BasicSelect
          name="cliente.rgUfId"
          label="UF de emissão"
          options={toOptions(ufs)}
          placeholder="Selecione"
          error={clienteErrors.rgUfId?.message}
        />

        <BasicSelect
          name="cliente.sexo"
          label="Sexo"
          options={toOptions(genders)}
          placeholder="Selecione"
          error={clienteErrors.sexo?.message}
        />
        <BasicSelect
          name="cliente.estadoCivil"
          label="Estado civil"
          options={toOptions(maritalStatuses)}
          placeholder="Selecione"
          error={clienteErrors.estadoCivil?.message}
        />

        {requiresSpouse && (
          <BasicInput
            name="cliente.nomeConjuge"
            label="Nome do cônjuge"
            error={clienteErrors.nomeConjuge?.message}
          />
        )}

        <BasicSelect
          name="cliente.nacionalidadeId"
          label="Nacionalidade"
          options={toOptions(nationalities)}
          placeholder="Selecione"
          error={clienteErrors.nacionalidadeId?.message}
        />
        <BasicSelect
          name="cliente.grauInstrucaoId"
          label="Grau de instrução"
          options={toOptions(educationLevels)}
          placeholder="Selecione"
          error={clienteErrors.grauInstrucaoId?.message}
        />

        <BasicSelect
          name="cliente.naturalidadeUfId"
          label="UF de nascimento"
          options={toOptions(ufs)}
          placeholder="Selecione"
          error={clienteErrors.naturalidadeUfId?.message}
        />
        <CityInput
          name="cliente.naturalidadeCidade"
          idName="cliente.naturalidadeCidadeId"
          label="Cidade de nascimento"
          uf={birthUf}
          error={
            clienteErrors.naturalidadeCidade?.message ??
            clienteErrors.naturalidadeCidadeId?.message
          }
        />

        <BasicSelect
          name="cliente.pep"
          label="Pessoa politicamente exposta"
          options={[
            { label: "Não", value: "false" },
            { label: "Sim", value: "true" },
          ]}
          placeholder="Selecione"
          error={clienteErrors.pep?.message}
        />
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          className="rounded-full px-8 py-3 font-semibold text-white"
          onClick={handleNext}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
